/**
 * AI CONTEXT
 * Purpose: loads and displays previous evaluation in read-only form layout.
 * Depends on: usePrevEval data hook and EvaluationForm in previous mode.
 * Used by: EvaluationFormModal previous-eval tab.
 * Importance: prior-cycle context during current eval decisions.
 * Refactor: decouple loading/error wrapper from display component for reuse.
 */
import { Form, Spin, Typography } from "antd"
import { noop } from "lodash"
import { useEffect } from "react"

import Flex from "#blocks/Flex"
import FormWithValues from "#components/FormWithValues"
import {
  EvaluationConstructor,
  EvaluationTrait,
} from "#customTypes/composedTypes/evaluationTypes"
import { Uuid } from "#customTypes/globalTypes"
import usePreviousEval from "#hooks/newHooks/usePrevEval"
import useErrorModal from "#hooks/useErrorModal"

import EvaluationForm from "./EvaluationForm"

const { Text } = Typography

const PreviousEvalFormBase = ({
  previousEval,
  previousEvalTraitsById,
}: {
  previousEval: EvaluationConstructor | null
  previousEvalTraitsById: Record<Uuid, EvaluationTrait>
}) => {
  if (!previousEval)
    return (
      <Flex center style={{ marginTop: 70, marginRight: 30 }}>
        <Text>No previous evaluation found</Text>
      </Flex>
    )

  return (
    <EvaluationForm
      initialValues={previousEval}
      evalTraitsById={previousEvalTraitsById}
      isPreviousEval
    />
  )
}

const PreviousEvalForm = ({
  currentEval,
}: {
  currentEval: EvaluationConstructor
}) => {
  const form = Form.useFormInstance()
  const { previousEval, previousEvalTraitsById, isReady, errors } =
    usePreviousEval(
      currentEval.studentId,
      currentEval.classGroup,
      currentEval.semester
    )

  const openErrorModal = useErrorModal("loading previous evaluation")
  useEffect(() => {
    if (errors) {
      openErrorModal(errors, "loading previous evaluation")
    }
  }, [errors, openErrorModal])

  if (!isReady) {
    return (
      <Flex center style={{ width: "100%", height: "30%" }}>
        <Spin style={{ transform: "scale(2)" }} />
      </Flex>
    )
  }

  return (
    <FormWithValues
      form={form}
      onFinish={noop}
      name="previousEval"
      layout="vertical"
      initialValues={previousEval ?? undefined}
      clearOnDestroy
    >
      <PreviousEvalFormBase
        previousEval={previousEval}
        previousEvalTraitsById={previousEvalTraitsById}
      />
    </FormWithValues>
  )
}

export default PreviousEvalForm
